import { createRouter, createWebHistory, RouterView } from "vue-router";
import type { RouteRecordRaw } from "vue-router";
import Login from "./views/login/Login";
import OAuthCallback from "./views/oauth/OAuthCallback";
import { useAuthStore } from "./stores/auth";
import { hasScope } from "./utils/jwt";
import UserNewPassword from "./views/grants/UserNewPassword";
import PageNotFound from "./components/PageNotFound";
import ProfileEdit from "./views/ProfileEdit";
import RoleIndex from "@/views/roles/RoleIndex";
import RoleEdit from "@/views/roles/RoleEdit";
import CompanyIndex from "@/views/company/CompanyIndex";
import CompanyCreate from "@/views/company/CreateCompany";
import CompanyEdit from "@/views/company/CompanyEdit";
import TenantDetails from "@/views/company/TenantDetails";
import TenantBranding from "@/views/company/TenantBranding";
import TenantEntitlements from "@/views/company/TenantEntitlements.vue";
import PrimaryGrantsList from "@/views/grants/PrimaryGrantsList";
import PrimaryGrantUpdate from "@/views/grants/PrimaryGrantUpdate";
import CompanyDetails from "@/views/company/CompanyDetails";
import TenantGrantsList from "@/views/company/TenantGrantsList";
import TenantGrantUpdate from "@/views/company/TenantGrantUpdate";
import SoftwareIndex from "@/views/software/SoftwareIndex";
import PreregistrationIndex from "@/views/preregistrations/PreregistrationIndex";
import PreregistrationDetails from "@/views/preregistrations/PreregistrationDetails";
import BillingMetricsIndex from "@/views/billing/BillingMetricsIndex.vue";
import BillingMetricsDetail from "@/views/billing/BillingMetricsDetail.vue";

const routes: RouteRecordRaw[] = [
  {
    path: "/",
    name: "home",
    component: BillingMetricsIndex,
    meta: {
      breadcrumb: { title: "Relatórios" },
    },
  },
  {
    path: "/login",
    name: "login",
    component: Login,
    meta: {
      public: true,
    },
  },
  {
    path: "/oauth/callback",
    name: "oauth.callback",
    component: OAuthCallback,
    meta: {
      public: true,
    },
  },
  {
    path: "/auth/login",
    name: "legacy.account.confirmation",
    component: Login,
    meta: {
      public: true,
    },
  },
  {
    path: "/confirm-email",
    name: "user.confirmation",
    component: UserNewPassword,
    meta: {
      public: true,
      new_user: true,
    },
  },
  {
    path: "/reset-password",
    name: "user.password",
    component: UserNewPassword,
    meta: {
      public: true,
    },
  },

  {
    path: "/tenants",
    component: RouterView,
    children: [
      {
        path: "",
        name: "tenant.index",
        component: CompanyIndex,
        meta: {
          can: "company:read",
          type: "tenant",
          breadcrumb: { title: "Clientes" },
        },
      },
      {
        path: "new",
        name: "tenant.create",
        component: CompanyCreate,
        meta: {
          can: "company:create",
          type: "tenant",
          breadcrumb: { title: "Novo Cliente", parent: "tenant.index" },
        },
      },
      {
        path: ":tenant_id",
        component: TenantDetails,
        meta: {
          can: "company:read",
        },
        children: [
          {
            path: "",
            name: "tenant.detail",
            component: CompanyDetails,
            meta: {
              can: "company:read",
              breadcrumb: { parent: "tenant.index", dynamicTitle: true, fallbackTitle: "Cliente" },
            },
          },
          {
            path: "companies",
            component: RouterView,
            children: [
              {
                path: "",
                name: "tenant.companies",
                component: CompanyIndex,
                meta: {
                  type: "companies",
                  can: "company:read",
                  breadcrumb: { title: "Empresas", parent: "tenant.detail" },
                },
              },
              {
                path: "new",
                name: "company.create",
                component: CompanyCreate,
                meta: {
                  can: "company:create",
                  type: "company",
                  breadcrumb: { title: "Nova Empresa", parent: "tenant.companies" },
                },
              },
              {
                path: ":company_id",
                component: RouterView,
                children: [
                  {
                    path: "",
                    name: "company.edit",
                    component: CompanyEdit,
                    meta: {
                      type: "company",
                      can: "company:read",
                      breadcrumb: { parent: "tenant.companies", dynamicTitle: true, fallbackTitle: "Empresa" },
                    },
                  },
                ],
              },
            ],
          },

          {
            path: "grants",
            component: RouterView,
            children: [
              {
                path: "",
                name: "tenant.grants",
                component: TenantGrantsList,
                meta: {
                  can: "grant:read",
                  breadcrumb: { title: "Acessos", parent: "tenant.detail" },
                },
              },
              {
                path: ":user_id",
                name: "tenant.grants.edit",
                component: TenantGrantUpdate,
                meta: {
                  can: "grant:read",
                  breadcrumb: { title: "Editar Acesso", parent: "tenant.grants" },
                },
              },
            ],
          },

          {
            path: "edit",
            name: "tenant.edit",
            component: CompanyEdit,
            meta: {
              type: "tenant",
              can: "company:edit",
              breadcrumb: { title: "Editar", parent: "tenant.detail" },
            },
          },
          {
            path: "branding",
            name: "tenant.branding",
            component: TenantBranding,
            meta: {
              can: "company:read",
              breadcrumb: { title: "Whitelabel", parent: "tenant.detail" },
            },
          },
          {
            path: "entitlements",
            name: "tenant.entitlements",
            component: TenantEntitlements,
            meta: {
              can: "company:read",
              breadcrumb: { title: "Módulos", parent: "tenant.detail" },
            },
          },
          {
            path: "billing",
            name: "tenant.billing",
            component: BillingMetricsDetail,
            meta: {
              can: "company:read",
              breadcrumb: { title: "Relatórios", parent: "tenant.detail" },
            },
          },
        ],
      },
    ],
  },

  {
    path: "/preregistrations",
    name: "preregistrations.index",
    component: PreregistrationIndex,
    meta: {
      breadcrumb: { title: "Pre-cadastros" },
    },
  },
  {
    path: "/preregistrations/:loja_cnpj",
    name: "preregistrations.details",
    component: PreregistrationDetails,
    meta: {
      breadcrumb: { title: "Detalhes", parent: "preregistrations.index" },
    },
  },

  {
    path: "/grants",
    name: "grants.index",
    component: PrimaryGrantsList,
    meta: {
      can: "grant:read",
      breadcrumb: { title: "Acessos" },
    },
  },
  {
    path: "/grants/:user_id",
    name: "grants.edit",
    component: PrimaryGrantUpdate,
    meta: {
      can: "grant:read",
      breadcrumb: { title: "Editar Acesso", parent: "grants.index" },
    },
  },
  {
    path: "/profile",
    name: "profile",
    component: ProfileEdit,
    meta: {
      breadcrumb: { title: "Editar Perfil" },
    },
  },
  {
    path: "/roles",
    name: "roles.index",
    component: RoleIndex,
    meta: {
      breadcrumb: { title: "Perfis" },
      context: "vendor",
    },
  },
  {
    path: "/roles/new",
    name: "roles.create",
    component: RoleEdit,
    meta: {
      can: "role:create",
      context: "vendor",
      breadcrumb: { title: "Novo Perfil", parent: "roles.index" },
    },
  },

  {
    path: "/roles/:role_id",
    name: "roles.edit",
    component: RoleEdit,
    meta: {
      can: "role:read",
      context: "vendor",
      breadcrumb: { title: "Editar Perfil", parent: "roles.index" },
    },
  },
  {
    path: "/billing",
    redirect: { name: "home" },
  },
  {
    path: "/software",
    name: "software.index",
    component: SoftwareIndex,
    meta: {
      breadcrumb: { title: "Software" },
      scope: "ops:software:upload",
    },
  },
  { path: "/:pathMatch(.*)*", name: "not-found", component: PageNotFound },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

router.beforeEach((to, from, next) => {
  const authStore = useAuthStore();

  if (!to.matched.some((record) => record.meta.public)) {
    if (!authStore.token) {
      next({
        name: "login",
        params: { next: to.fullPath },
      });
    } else {
      // Check for required scopes
      const requiredScope = to.matched.find((record) => record.meta.scope)?.meta
        .scope as string | undefined;
      if (requiredScope && !hasScope(authStore.token, requiredScope)) {
        next({
          name: "home",
        });
      } else {
        next();
      }
    }
  } else {
    next();
  }
});

export default router;
